import React, { useEffect, useState } from 'react';
import CallToActionLinkAtom from '@atoms/link-atoms/call-to-action-link-atom';
import TabTitles from '@blocks/tabbed-block/tab-titles';
import RichText from '@components/rich-text/rich-text';
import useScrollArrows from '@hooks/use-scroll-arrows';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { H2Atom, HighlightAtom, PAtom } from '@atoms/text-atoms/text-atoms';
import {
  LeftScrollArrow,
  RightScrollArrow,
} from '@components/scroll-arrows/scroll-arrows';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { CircularExamplesBlockProps } from './circular-examples-block.types';
import { CircularExamplesWrapper } from './circular-examples-block.styles';

const CircularExamplesBlock: React.FC<CircularExamplesBlockProps> = ({
  anchorId,
  title,
  subtitle = '',
  examples,
  colour = vars.coreDarkGrey,
  className,
  translate = true,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabsRef = React.useRef<HTMLUListElement>(null);
  const [mappedTabs, setMappedTabs] = useState<
    { id: string; tabTitle: string }[]
  >([]);
  const { showLeftArrow, showRightArrow, scrollTabs } = useScrollArrows({
    tabsRef,
  });
  const currentExample = examples[activeTab];
  const gatsbyImage = getImage(currentExample.image.gatsbyImageData);
  const gatsbyLogoImage = getImage(currentExample.logo.gatsbyImageData);

  const handleTabClick = index => {
    setActiveTab(index);
  };

  useEffect(() => {
    setMappedTabs([
      ...examples.map(example => {
        return {
          id: example.id,
          tabTitle: example.tabTitle,
        };
      }),
    ]);
  }, [examples, tabsRef]);

  if (examples.length > 0) {
    return (
      <CircularExamplesWrapper
        id={anchorId}
        className={className}
        data-testid="examples-block"
        colour={colour}
      >
        <div className="component-grid-structure">
          <H2Atom className="block-title">{title}</H2Atom>
          {subtitle && <PAtom className="block-subtitle">{subtitle}</PAtom>}

          <div className="circular-examples-tabs-container">
            {showLeftArrow && (
              <LeftScrollArrow
                showLeftArrow={showLeftArrow}
                scrollTabs={scrollTabs}
              />
            )}
            {showRightArrow && (
              <RightScrollArrow
                showRightArrow={showRightArrow}
                scrollTabs={scrollTabs}
              />
            )}
            <TabTitles
              tabs={mappedTabs}
              activeTab={activeTab}
              onTabClick={handleTabClick}
              parentRef={tabsRef}
              gtmId={GTM_ID.CIRCULAR_EXAMPLES_TAB}
            />
          </div>

          <div className="tabs-separator" />

          <div className="left-column">
            {currentExample.quote && (
              <HighlightAtom>{currentExample.quote}</HighlightAtom>
            )}
            <RichText content={currentExample.body} />
            {currentExample.cta.url && currentExample.cta.text && (
              <CallToActionLinkAtom
                data-gtm-id={GTM_ID.CIRCULAR_EXAMPLES_EXPLORE_EXAMPLE}
                url={currentExample.cta.url}
                buttonText={Translate(currentExample.cta.text, translate)}
                highlightColour={colour}
              />
            )}
          </div>
          <div className="right-column">
            {gatsbyLogoImage &&
            currentExample.logo?.format !== 'image/gif' &&
            currentExample.logo?.format !== 'image/svg+xml' ? (
              <div className="logo-wrapper">
                <GatsbyImage
                  image={gatsbyLogoImage}
                  alt={currentExample.logo.alt ? currentExample.logo.alt : ''}
                  className="gatsby-image-pos"
                />
              </div>
            ) : (
              currentExample.logo.src && (
                <div className="logo-wrapper">
                  <img
                    src={currentExample.logo.src}
                    alt={currentExample.logo.alt ? currentExample.logo.alt : ''}
                  />
                </div>
              )
            )}
            {gatsbyImage &&
            currentExample.image?.format !== 'image/gif' &&
            currentExample.image?.format !== 'image/svg+xml' ? (
              <div className="image-wrapper">
                <GatsbyImage
                  image={gatsbyImage}
                  alt={currentExample.image.alt ? currentExample.image.alt : ''}
                  className="gatsby-image-pos"
                />
              </div>
            ) : (
              currentExample.image.src && (
                <div className="image-wrapper">
                  <img
                    src={currentExample.image.src}
                    alt={
                      currentExample.image.alt ? currentExample.image.alt : ''
                    }
                  />
                </div>
              )
            )}
          </div>
        </div>
      </CircularExamplesWrapper>
    );
  } else {
    console.error('Tile Block called with missing examples!');
    return null;
  }
};
export default CircularExamplesBlock;
